/**
 * Whitelabel User Integration Composable  
 * Integrates whitelabel feature toggles with existing user store patterns
 * Provides unified access to user-based and whitelabel-based feature availability
 */

import { computed, type ComputedRef } from 'vue'
import { useUserStore } from '../stores/userStore'
import { useWhitelabelFeatures } from './useWhitelabelConfig'

export interface UseWhitelabelUserIntegrationReturn {
  // Combined feature availability
  isLeaderboardAvailable: ComputedRef<boolean>
  isProfilePageAvailable: ComputedRef<boolean>
  isUserSwitchingAvailable: ComputedRef<boolean>
  isLanguageSwitchingAvailable: ComputedRef<boolean>
  areNotificationsEnabled: ComputedRef<boolean>
  
  // Advanced user segment with whitelabel integration
  isAdvancedUserWithFeatures: ComputedRef<boolean>
  isProfessionalUserWithFeatures: ComputedRef<boolean>
  
  // Feature validation helpers
  canUserAccessFeature: (feature: keyof ReturnType<typeof useWhitelabelFeatures>['features']) => boolean
  getUserFeatureStatus: () => {
    userSegment: string
    whitelabelFeatures: Record<string, boolean>
    combinedAvailability: Record<string, boolean>
  }
}

/**
 * Composable for integrating whitelabel features with user store patterns
 * Addresses Subtask 7.2: Integrate Whitelabel Features with Existing User Store
 */
export function useWhitelabelUserIntegration(): UseWhitelabelUserIntegrationReturn {
  const userStore = useUserStore()
  const whitelabelFeatures = useWhitelabelFeatures()
  
  // Combined feature availability - both whitelabel AND user requirements must be met
  const isLeaderboardAvailable = computed(() => {
    return whitelabelFeatures.isFeatureEnabled('showLeaderboard') && 
           userStore.userSegment === 'advanced'
  })
  
  const isProfilePageAvailable = computed(() => {
    return whitelabelFeatures.isFeatureEnabled('showProfilePage')
  })
  
  const isUserSwitchingAvailable = computed(() => {
    return whitelabelFeatures.isFeatureEnabled('allowUserSwitching')
  })
  
  const isLanguageSwitchingAvailable = computed(() => {
    return whitelabelFeatures.isFeatureEnabled('allowLanguageSwitching')
  })
  
  const areNotificationsEnabled = computed(() => {
    return whitelabelFeatures.isFeatureEnabled('enableNotifications')
  })
  
  // Advanced user segment with whitelabel integration
  const isAdvancedUserWithFeatures = computed(() => {
    return userStore.userSegment === 'advanced' && 
           whitelabelFeatures.isFeatureEnabled('showLeaderboard')
  })
  
  const isProfessionalUserWithFeatures = computed(() => {
    return userStore.userSegment === 'professional'
    // Note: No specific whitelabel features tied to professional segment yet
  })
  
  // Feature validation helper
  function canUserAccessFeature(feature: keyof ReturnType<typeof useWhitelabelFeatures>['features']): boolean {
    const isFeatureEnabled = whitelabelFeatures.isFeatureEnabled(feature)
    
    // Special logic for features that depend on user segment
    switch (feature) {
      case 'showLeaderboard':
        return isFeatureEnabled && userStore.userSegment === 'advanced'
      default:
        return isFeatureEnabled
    }
  }
  
  // Debug and status helper
  function getUserFeatureStatus() {
    return {
      userSegment: userStore.userSegment,
      whitelabelFeatures: {
        showLeaderboard: whitelabelFeatures.isFeatureEnabled('showLeaderboard'),
        allowUserSwitching: whitelabelFeatures.isFeatureEnabled('allowUserSwitching'),
        allowLanguageSwitching: whitelabelFeatures.isFeatureEnabled('allowLanguageSwitching'),
        showProfilePage: whitelabelFeatures.isFeatureEnabled('showProfilePage'),
        enableNotifications: whitelabelFeatures.isFeatureEnabled('enableNotifications')
      },
      combinedAvailability: {
        leaderboard: isLeaderboardAvailable.value,
        profilePage: isProfilePageAvailable.value,
        userSwitching: isUserSwitchingAvailable.value,
        languageSwitching: isLanguageSwitchingAvailable.value,
        notifications: areNotificationsEnabled.value
      }
    }
  }
  
  return {
    // Combined feature availability
    isLeaderboardAvailable,
    isProfilePageAvailable,
    isUserSwitchingAvailable,
    isLanguageSwitchingAvailable,
    areNotificationsEnabled,
    
    // Advanced user segment with whitelabel integration
    isAdvancedUserWithFeatures,
    isProfessionalUserWithFeatures,
    
    // Helper functions
    canUserAccessFeature,
    getUserFeatureStatus
  }
}